import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';
import { AlarmGroupService } from '@service/alarm-group.service';
import { DutyEngineerService } from '@service/duty-engineer.service';
import { EapService } from '@service/eap.service';
import { EapLedConfiguration, EapLedFunction, EapLedPattern, eapLedFunction } from 'icreate/common/models/eap';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { eapLedPatternValidator } from 'icreate/common/validators/eap-led-pattern/eap-led-pattern-validator';
import _ from 'lodash';
import { lastValueFrom, take } from 'rxjs';

@Component({
    selector: 'app-new-pattern-setting-dialog',
    templateUrl: './new-pattern-setting-dialog.component.html',
    styleUrl: './new-pattern-setting-dialog.component.css',
    standalone: false,
})
export class NewPatternSettingDialog {
    readonly PATTERN_NAME = 'pattern name';

    readonly manUnmanOptions: SimpleDropdownOption[] = [
        {
            value: 'MAN',
            string: 'system-settings.eap.led.pattern.configuration.man',
        },
        {
            value: 'UNMAN',
            string: 'system-settings.eap.led.pattern.configuration.unman',
        },
    ];

    readonly onDutyOptions: SimpleDropdownOption[] = [
        {
            value: 'ON_DUTY',
            string: 'system-settings.eap.led.pattern.configuration.on-duty',
        },
        {
            value: 'NOT_ON_DUTY',
            string: 'system-settings.eap.led.pattern.configuration.not-on-duty',
        },
    ];

    @ViewChild('cancelConfirmation')
    cancelConfirmation: ConfirmationWindowComponent;

    ledPattern: EapLedPattern;
    initialLedPattern: EapLedPattern;

    ledConfigurations: EapLedConfiguration[] = [];

    isEditing = false;
    isSubmitting = false;

    validationErrors: ValidationErrorMap = {};

    alarmGroupOptions: SimpleDropdownOption[] = [];
    dutyRoleOptions: SimpleDropdownOption[] = [];

    readonly eapLedFunction = eapLedFunction;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            ledPattern?: EapLedPattern;
            vesselId: string;
        },
        private dialogRef: MatDialogRef<NewPatternSettingDialog>,
        private eapService: EapService,
        private alarmGroupService: AlarmGroupService,
        private dutyEngineerService: DutyEngineerService
    ) {}

    async ngOnInit(): Promise<void> {
        this.isEditing = !!this.data.ledPattern;
        if (this.isEditing) {
            this.ledPattern = _.cloneDeep(this.data.ledPattern);
        } else {
            this.ledPattern = {
                id: null,
                vesselId: this.data.vesselId,
                name: '',
                pattern: {
                    leds: [],
                },
            };
        }
        this.ledConfigurations = this.createLedConfigurations(this.ledPattern.pattern?.leds ?? []);
        this.updatePattern();
        this.initialLedPattern = _.cloneDeep(this.ledPattern);
        await this.loadDropdownOptions();
        this.updateValidationErrors();
    }

    private async loadDropdownOptions(): Promise<void> {
        const [alarmGroups, dutyEngineers] = await Promise.all([
            this.alarmGroupService.getAll(this.data.vesselId),
            this.dutyEngineerService.getList(this.data.vesselId),
        ]);

        this.alarmGroupOptions = alarmGroups.map(alarmGroup => ({
            value: alarmGroup.id,
            string: alarmGroup.name,
        }));

        this.dutyRoleOptions = dutyEngineers.map(engineer => ({
            value: engineer.id,
            string: engineer.name,
        }));
        this.clearStaleConfigurations();
        this.updatePattern();
    }

    private clearStaleConfigurations(): void {
        const alarmGroupIds = new Set(this.alarmGroupOptions.map(option => option.value));
        const dutyRoleIds = new Set(this.dutyRoleOptions.map(option => option.value));

        this.ledConfigurations.forEach(config => {
            if (config.alarmGroupId && !alarmGroupIds.has(config.alarmGroupId)) {
                config.alarmGroupId = null;
            }

            if (config.dutyRole && !dutyRoleIds.has(config.dutyRole)) {
                config.dutyRole = null;
            }
        });
    }

    private createLedConfigurations(assignedLeds: EapLedConfiguration[]): EapLedConfiguration[] {
        return Array.from({ length: 16 }, (_, index) => {
            const led = index + 1;

            return (
                assignedLeds.find(config => config.led === led) ?? {
                    led,
                    function: null,
                }
            );
        });
    }

    get isSaveDisabled(): boolean {
        return _.isEqual(this.initialLedPattern, this.ledPattern) || Object.keys(this.validationErrors).length > 0;
    }

    get functionOptions(): SimpleDropdownOption[] {
        return [
            {
                value: eapLedFunction.ALARM,
                string: 'system-settings.eap.led.pattern.function.alarm',
            },
            {
                value: eapLedFunction.MAN_UNMAN,
                string: 'system-settings.eap.led.pattern.function.man-unman',
            },
            {
                value: eapLedFunction.WHO_DUTY,
                string: 'system-settings.eap.led.pattern.function.who-duty',
            },
            {
                value: eapLedFunction.ON_DUTY,
                string: 'system-settings.eap.led.pattern.function.on-duty',
            },
            {
                value: eapLedFunction.ENGINEER_CALL,
                string: 'system-settings.eap.led.pattern.function.engineer-call',
            },
        ];
    }

    updateValidationErrors(): void {
        const validationData = {
            ...(this.isEditing && {
                id: this.ledPattern.id,
            }),
            vesselId: this.ledPattern.vesselId,
            name: this.ledPattern.name?.trim(),
            pattern: this.ledPattern.pattern,
        };

        const errors = getValidationErrors(eapLedPatternValidator(), validationData);
        const mappedErrors: ValidationErrorMap = mapErrors(errors) || {};
        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : {};
    }

    getFunctionOptions(led: number): SimpleDropdownOption[] {
        const allowedFunctions: Record<number, EapLedFunction[]> = {
            1: [eapLedFunction.ALARM],
            2: [eapLedFunction.ALARM],
            3: [eapLedFunction.ALARM],
            4: [eapLedFunction.ALARM],
            5: [eapLedFunction.ALARM],
            6: [eapLedFunction.ALARM],
            7: [eapLedFunction.ALARM],
            8: [eapLedFunction.ALARM],
            9: [eapLedFunction.ALARM, eapLedFunction.MAN_UNMAN, eapLedFunction.WHO_DUTY, eapLedFunction.ON_DUTY],
            10: [eapLedFunction.ALARM, eapLedFunction.MAN_UNMAN, eapLedFunction.WHO_DUTY, eapLedFunction.ON_DUTY],
            11: [eapLedFunction.ALARM, eapLedFunction.WHO_DUTY, eapLedFunction.ON_DUTY],
            12: [eapLedFunction.ALARM, eapLedFunction.WHO_DUTY, eapLedFunction.ON_DUTY],
            13: [eapLedFunction.MAN_UNMAN, eapLedFunction.WHO_DUTY, eapLedFunction.ON_DUTY],
            14: [eapLedFunction.MAN_UNMAN, eapLedFunction.WHO_DUTY],
            15: [eapLedFunction.WHO_DUTY],
            16: [eapLedFunction.WHO_DUTY, eapLedFunction.ENGINEER_CALL],
        };

        return (allowedFunctions[led] ?? []).map(value => ({
            value,
            string: `system-settings.eap.led.pattern.function.${value.toLowerCase().replace('_', '-')}`,
        }));
    }

    onFunctionChanged(config: EapLedConfiguration, event: { value: EapLedFunction }): void {
        this.resetConfiguration(config, event.value);
    }

    onAlarmGroupChanged(config: EapLedConfiguration, event: { value: string }): void {
        config.alarmGroupId = event.value;
        this.resetPattern();
    }

    onDutyRoleChanged(config: EapLedConfiguration, event: { value: string }): void {
        config.dutyRole = event.value;
        this.resetPattern();
    }

    onManUnmanChanged(config: EapLedConfiguration, event: { value: string }): void {
        config.manUnman = event.value as 'MAN' | 'UNMAN';
        this.resetPattern();
    }

    onOnDutyChanged(config: EapLedConfiguration, event: { value: string }): void {
        config.onDuty = event.value as 'ON_DUTY' | 'NOT_ON_DUTY';
        this.resetPattern();
    }

    onInputChange(event: any): void {
        if (event?.name === this.PATTERN_NAME) {
            this.ledPattern.name = event.value;
        }
        this.updateValidationErrors();
    }

    getSelectedFunctionOption(config: EapLedConfiguration): EapLedFunction | null {
        return config.function ?? null;
    }

    clearFunction(config: EapLedConfiguration): void {
        this.resetConfiguration(config);
    }

    resetConfiguration(config, fun?) {
        config.function = fun ?? null;
        config.alarmGroupId = null;
        config.onDuty = null;
        config.manUnman = null;
        config.dutyRole = null;

        this.resetPattern();
    }

    resetPattern() {
        this.updatePattern();
        this.updateValidationErrors();
    }

    private updatePattern(): void {
        this.ledPattern.pattern.leds = this.ledConfigurations.map(config => ({
            ...config,
            function: config.function ?? null,
        }));
    }

    getDutyRoleOption(config: EapLedConfiguration): string | null {
        if (!config.dutyRole) {
            return null;
        }

        const exists = this.dutyRoleOptions.some(option => option.value === config.dutyRole);
        return exists ? config.dutyRole : null;
    }

    getAlarmGroupOption(config: EapLedConfiguration): string | null {
        if (!config.alarmGroupId) {
            return null;
        }

        const exists = this.alarmGroupOptions.some(option => option.value === config.alarmGroupId);
        return exists ? config.alarmGroupId : null;
    }

    getManUnmanOption(config: EapLedConfiguration): string | null {
        return config.manUnman ?? null;
    }

    getOnDutyOption(config: EapLedConfiguration): string | null {
        return config.onDuty ?? null;
    }

    hasConfiguration(config: EapLedConfiguration): boolean {
        return !!(
            this.getAlarmGroupOption(config) ||
            this.getOnDutyOption(config) ||
            this.getManUnmanOption(config) ||
            this.getDutyRoleOption(config)
        );
    }

    clearConfiguration(config: EapLedConfiguration): void {
        this.resetConfiguration(config, config.function);
    }

    async save(): Promise<void> {
        if (this.isSaveDisabled || this.isSubmitting) {
            return;
        }
        this.updatePattern();
        this.updateValidationErrors();

        if (Object.keys(this.validationErrors).length > 0) {
            return;
        }

        this.isSubmitting = true;
        try {
            let savedPattern: any;
            if (this.isEditing) {
                savedPattern = await this.eapService.updateLedPattern(this.ledPattern);
            } else {
                savedPattern = await this.eapService.createLedPattern(this.ledPattern);
            }
            this.dialogRef.close(savedPattern);
        } catch (error) {
            console.error(error);
        } finally {
            this.isSubmitting = false;
        }
    }

    async cancel(): Promise<boolean> {
        if (this.isSaveDisabled) {
            this.dialogRef.close();
            return true;
        }

        this.cancelConfirmation.setVisible(true);
        const confirmationOption = await lastValueFrom(this.cancelConfirmation.onOptionSelected.pipe(take(1)));
        switch (confirmationOption) {
            case ConfirmationWindowTypes.DISCARD:
                this.dialogRef.close();
                return true;

            case ConfirmationWindowTypes.GO_BACK:
                return false;

            case ConfirmationWindowTypes.SAVE:
                await this.save();
                return true;

            default:
                return false;
        }
    }

    get occupiedLedCount(): number {
        return this.ledConfigurations.filter(config => config.function != null).length;
    }
}
